import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Table, Button, Space, Tag, Popconfirm, message, Card, Statistic,
  Spin, Empty, Modal, Form, Input, Select, Divider, Typography, DatePicker, Upload, Image, Dropdown, Tooltip,
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined, EditOutlined, DeleteOutlined, CameraOutlined, EyeOutlined, EyeInvisibleOutlined, BankOutlined, PrinterOutlined } from '@ant-design/icons';
import { decryptData } from '../../utils/crypto';
import {
  getAccountByIdApi, getBuyerDetailApi, addTransactionApi, updateTransactionApi,
  deleteTransactionApi, updateTransactionStatusApi, getShopkeeperDetailApi,
} from '../../api/accounts';
import { createDailyExpenseApi } from '../../api/dailyExpense';
import { changeExpensePaymentStatusApi, addKharchoPaymentLogApi, addKharchoPaymentLogImageApi, deleteKharchoPaymentLogApi } from '../../api/expenses';
import { addVechanPaymentLogApi, deleteVechanPaymentLogApi, updateVechanPaymentImageApi } from '../../api/sales';
import { formatCurrency, formatDate, escapeHtml } from '../../utils/formatters';
import { CROP_NAME_IMAGE, PAYMENT_LOG_IMAGE } from '../../utils/constants';
import dayjs from 'dayjs';

const { Title } = Typography;

const STATUS_COLOR = { paid: 'green', unpaid: 'orange', na: 'default' };
const STATUS_LABEL = { paid: 'Paid', unpaid: 'Unpaid', na: 'N/A' };
const SALE_STATUS_COLOR = { credit: 'green', partial: 'blue', debit: 'orange' };
const SALE_STATUS_LABEL = { credit: 'Received', partial: 'Partial', debit: 'Outstanding' };

const STATUS_OPTIONS = [
  { value: 'all',    label: 'All' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'paid',   label: 'Paid' },
];

const EMPTY_PAY = { amount: '', date: dayjs(), note: '', image: null };

// ── Add Payment Modal ─────────────────────────────────────────────────────
function AddPaymentModal({ open, onClose, onSubmit, loading, remaining }) {
  const [form, setForm] = useState(EMPTY_PAY);
  useEffect(() => { if (open) setForm(EMPTY_PAY); }, [open]);

  const amountNum = Number(form.amount);
  const exceedsRemaining = form.amount !== '' && amountNum > Number(remaining);

  return (
    <Modal
      title="Add Payment"
      open={open} onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="ok" type="primary" loading={loading} disabled={exceedsRemaining}
          onClick={() => {
            if (!form.amount || !form.date) { message.warning('Enter amount and date'); return; }
            if (exceedsRemaining) { message.warning(`Amount cannot exceed remaining ${formatCurrency(remaining)}`); return; }
            onSubmit(form);
          }}
          style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>Save</Button>,
      ]}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
        {remaining != null && (
          <div style={{ background: '#fff7e6', borderRadius: 6, padding: '6px 12px', fontSize: 13 }}>
            Remaining: <strong style={{ color: '#fa8c16' }}>{formatCurrency(remaining)}</strong>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <div style={{ fontSize: 13, marginBottom: 4 }}>Amount (₹)</div>
            <Input
              type="number" placeholder="0"
              value={form.amount}
              status={exceedsRemaining ? 'error' : ''}
              onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} />
            {exceedsRemaining && (
              <div style={{ fontSize: 11, color: '#ff4d4f', marginTop: 2 }}>Exceeds remaining amount</div>
            )}
          </div>
          <div>
            <div style={{ fontSize: 13, marginBottom: 4 }}>Date</div>
            <DatePicker value={form.date} onChange={(d) => setForm((p) => ({ ...p, date: d }))} style={{ width: '100%' }} />
          </div>
        </div>
        <div>
          <div style={{ fontSize: 13, marginBottom: 4 }}>Remark (optional)</div>
          <Input placeholder="Add a remark"
            value={form.note}
            onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} />
        </div>
        <Upload
          beforeUpload={(file) => { setForm((p) => ({ ...p, image: file })); return false; }}
          maxCount={1} accept="image/*" showUploadList={false}
        >
          <Button icon={<CameraOutlined />}>
            {form.image ? form.image.name.slice(0, 18) + '…' : 'Attach Photo (optional)'}
          </Button>
        </Upload>
      </div>
    </Modal>
  );
}

// ── Transaction modal for general ─────────────────────────────────────────
function TransactionModal({ open, onClose, onSubmit, initialValues, loading }) {
  const [form] = Form.useForm();
  useEffect(() => {
    if (open) {
      form.setFieldsValue(initialValues ? { ...initialValues } : { type: 'credit', date: dayjs().format('YYYY-MM-DD') });
    } else {
      form.resetFields();
    }
  }, [open, initialValues, form]);

  return (
    <Modal
      title={initialValues?.id ? 'Edit Transaction' : 'Add Transaction'}
      open={open} onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="ok" type="primary" loading={loading}
          onClick={() => form.validateFields().then(onSubmit)}
          style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>Save</Button>,
      ]}
    >
      <Form form={form} layout="vertical" size="large">
        <Form.Item label="Note" name="note">
          <Input placeholder="Optional note" />
        </Form.Item>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item label="Amount (₹)" name="amount" rules={[{ required: true }]}>
            <Input type="number" placeholder="0" />
          </Form.Item>
          <Form.Item label="Type" name="type" rules={[{ required: true }]}>
            <Select options={[
              { value: 'credit', label: 'Credit (Received)' },
              { value: 'debit',  label: 'Debit (Given)' },
            ]} />
          </Form.Item>
        </div>
        <Form.Item label="Date" name="date" rules={[{ required: true }]}>
          <Input type="date" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Payment detail modal for labour / shop_keeper ─────────────────────────
function PaymentDetailModal({ open, onClose, txn, accountType, onStatusChange, statusLoading }) {
  if (!txn) return null;
  const status = txn.payment_status || txn.status;

  return (
    <Modal title="Payment Detail" open={open} onCancel={onClose} footer={null} width={400}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {txn.crop_name && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#888' }}>Crop</span>
            <span style={{ fontWeight: 600 }}>{txn.crop_name}</span>
          </div>
        )}
        {(txn.note || txn.expense_type || txn.type) && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#888' }}>Type / Note</span>
            <span style={{ fontWeight: 600 }}>{txn.note || txn.expense_type || txn.type}</span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: '#888' }}>Amount</span>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{formatCurrency(txn.amount)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: '#888' }}>Date</span>
          <span>{formatDate(txn.date || txn.created_at)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#888' }}>Status</span>
          <Tag color={STATUS_COLOR[status] || 'default'}>{STATUS_LABEL[status] || status}</Tag>
        </div>
        {txn.paid_at && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#888' }}>Paid At</span>
            <span>{formatDate(txn.paid_at)}</span>
          </div>
        )}
        <Divider style={{ margin: '8px 0' }} />
        <div style={{ display: 'flex', gap: 8 }}>
          {status !== 'paid' && (
            <Button type="primary" loading={statusLoading}
              style={{ background: '#52c41a', borderColor: '#52c41a', flex: 1 }}
              onClick={() => onStatusChange(txn, 'paid')}>Mark Paid</Button>
          )}
          {status !== 'unpaid' && (
            <Button loading={statusLoading} style={{ flex: 1 }}
              onClick={() => onStatusChange(txn, 'unpaid')}>Mark Unpaid</Button>
          )}
          {accountType === 'labour' && status !== 'na' && (
            <Button loading={statusLoading} style={{ flex: 1 }}
              onClick={() => onStatusChange(txn, 'na')}>N/A</Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ── Buyer: sales table with expandable transactions ────────────────────────
function BuyerSales({ sales, onReload }) {
  const [payOpen, setPayOpen]         = useState(false);
  const [payVechanId, setPayVechanId] = useState(null);
  const [payRemaining, setPayRemaining] = useState(null);
  const [payLoading, setPayLoading]   = useState(false);
  const [deletingId, setDeletingId]   = useState(null);
  const [imgLoadingId, setImgLoadingId] = useState(null);

  const handleUpdateImage = async (id, file) => {
    setImgLoadingId(id);
    const fd = new FormData();
    fd.append('payment_log_id', id);
    fd.append('payment_image', file);
    try {
      await updateVechanPaymentImageApi(fd);
      message.success('Image updated');
      onReload();
    } catch {
      message.error('Failed to update image');
    } finally {
      setImgLoadingId(null);
    }
  };

  const handleAddPayment = async (form) => {
    setPayLoading(true);
    const fd = new FormData();
    fd.append('vechan_id', payVechanId);
    fd.append('amount', form.amount);
    fd.append('payment_date', form.date.format('YYYY-MM-DD'));
    if (form.note) fd.append('note', form.note);
    if (form.image) fd.append('image', form.image);
    try {
      await addVechanPaymentLogApi(fd);
      message.success('Payment added');
      setPayOpen(false);
      onReload();
    } catch {
      message.error('Failed to add payment');
    } finally {
      setPayLoading(false);
    }
  };

  const handleDeleteTxn = async (id) => {
    setDeletingId(id);
    try {
      await deleteVechanPaymentLogApi(id);
      message.success('Deleted');
      onReload();
    } catch {
      message.error('Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  const txnColumns = [
    {
      title: 'Type', dataIndex: 'type', width: 110,
      render: (v) => <Tag color={v === 'credit' ? 'green' : 'orange'}>{v === 'credit' ? 'Received' : 'Outstanding'}</Tag>,
    },
    {
      title: 'Amount', dataIndex: 'amount',
      render: (v, r) => (
        <span style={{ fontWeight: 600, color: r.type === 'credit' ? '#52c41a' : '#ff4d4f' }}>
          {formatCurrency(v)}
        </span>
      ),
    },
    { title: 'Date', dataIndex: 'date', render: formatDate },
    { title: 'Remark', dataIndex: 'note', render: (v) => v || '—' },
    {
      title: 'Receipt', dataIndex: 'image', width: 70,
      render: (img, r) => {
        const src = r.image_url || (img ? PAYMENT_LOG_IMAGE(img) : null);
        return src ? (
          <div style={{ position: 'relative', width: 40, height: 40, flexShrink: 0 }}>
            <Image src={src} width={40} height={40} style={{ objectFit: 'cover', borderRadius: 4, display: 'block' }} />
            <label
              htmlFor={`buyer-img-${r.id}`}
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute', bottom: 1, right: 1, width: 16, height: 16,
                background: 'rgba(0,0,0,0.7)', borderRadius: 2, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <CameraOutlined style={{ color: '#fff', fontSize: 9 }} />
            </label>
            <input
              id={`buyer-img-${r.id}`}
              type="file" accept="image/*" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpdateImage(r.id, f); e.target.value = ''; }}
            />
          </div>
        ) : (
          <>
            <label
              htmlFor={`buyer-img-${r.id}`}
              onClick={(e) => e.stopPropagation()}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid #d9d9d9', borderRadius: 4, padding: '2px 7px',
                cursor: 'pointer', background: '#fff', height: 24,
              }}
            >
              {imgLoadingId === r.id ? <Spin size="small" /> : <CameraOutlined style={{ fontSize: 12 }} />}
            </label>
            <input
              id={`buyer-img-${r.id}`}
              type="file" accept="image/*" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpdateImage(r.id, f); e.target.value = ''; }}
            />
          </>
        );
      },
    },
    {
      title: '', key: 'actions', width: 50,
      render: (_, r) => r.type === 'credit' ? (
        <Popconfirm title="Delete this payment?" onConfirm={() => handleDeleteTxn(r.id)} okButtonProps={{ danger: true }}>
          <Button size="small" danger icon={<DeleteOutlined />} loading={deletingId === r.id} />
        </Popconfirm>
      ) : null,
    },
  ];

  const saleColumns = [
    {
      title: 'Crop', key: 'crop',
      render: (_, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {r.crop_image
            ? <img src={CROP_NAME_IMAGE(r.crop_image)} alt={r.crop} style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }} />
            : <span style={{ fontSize: 18 }}>🌱</span>
          }
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>
              {r.crop}{r.verity && <span style={{ fontWeight: 400, color: '#555', marginLeft: 4 }}>, {r.verity}</span>}
            </div>
            {r.plot_name && <div style={{ fontSize: 11, color: '#aaa' }}>📍 {r.plot_name}</div>}
          </div>
        </div>
      ),
    },
    { title: 'Date', dataIndex: 'sell_date', render: formatDate },
    { title: 'Amount', dataIndex: 'amount', render: (v) => <span style={{ fontWeight: 600 }}>{formatCurrency(v)}</span> },
    {
      title: 'Received', key: 'received',
      render: (_, r) => {
        const paid = (r.transactions || []).filter(t => t.type === 'credit').reduce((s, t) => s + Number(t.amount), 0);
        return <span style={{ color: '#52c41a' }}>{formatCurrency(paid || r.received_amount)}</span>;
      },
    },
    {
      title: 'Remaining', key: 'remaining',
      render: (_, r) => {
        const paid = (r.transactions || []).filter(t => t.type === 'credit').reduce((s, t) => s + Number(t.amount), 0);
        const rem = r.transactions ? Number(r.amount) - paid : Number(r.remaining);
        return <span style={{ color: rem > 0 ? '#fa8c16' : '#52c41a', fontWeight: 600 }}>{formatCurrency(rem)}</span>;
      },
    },
    {
      title: 'Status', dataIndex: 'payment_status',
      render: (v) => <Tag color={SALE_STATUS_COLOR[v] || 'default'}>{SALE_STATUS_LABEL[v] || v}</Tag>,
    },
  ];

  if (!sales?.length) return <Empty description="No sales" image={Empty.PRESENTED_IMAGE_SIMPLE} />;

  return (
    <>
      <Table
        dataSource={sales}
        columns={saleColumns}
        rowKey="vechan_id"
        scroll={{ x: 600 }}
        expandable={{
          expandRowByClick: true,
          expandedRowRender: (record) => (
            <div style={{ padding: '8px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 12, color: '#888' }}>Transactions</span>
                {(record.payment_status === 'partial' || record.payment_status === 'debit') && (
                  <Button
                    size="small" type="primary" icon={<PlusOutlined />}
                    style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}
                    onClick={() => {
                      const paid = (record.transactions || []).filter(t => t.type === 'credit').reduce((s, t) => s + Number(t.amount), 0);
                      setPayVechanId(record.vechan_id);
                      setPayRemaining(record.transactions ? Number(record.amount) - paid : Number(record.remaining));
                      setPayOpen(true);
                    }}
                  >
                    Add Payment
                  </Button>
                )}
              </div>
              <Table
                dataSource={record.transactions || []}
                columns={txnColumns}
                rowKey={(r, i) => r.id ?? i}
                pagination={false}
                size="small"
              />
            </div>
          ),
          rowExpandable: (record) => (record.transactions?.length > 0 || record.payment_status === 'partial' || record.payment_status === 'debit'),
        }}
      />

      <AddPaymentModal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        onSubmit={handleAddPayment}
        loading={payLoading}
        remaining={payRemaining}
      />
    </>
  );
}

// ── ShopKeeper: expenses table with expandable transactions ───────────────
function ShopkeeperExpenses({ expenses, onReload }) {
  const [payOpen, setPayOpen]           = useState(false);
  const [payKharchoId, setPayKharchoId] = useState(null);
  const [payRemaining, setPayRemaining] = useState(null);
  const [payLoading, setPayLoading]     = useState(false);
  const [imgLoadingId, setImgLoadingId] = useState(null);
  const [deletingId, setDeletingId]     = useState(null);
  const [expandedKeys, setExpandedKeys] = useState([]);

  const handleDeleteLog = async (id) => {
    setDeletingId(id);
    try {
      await deleteKharchoPaymentLogApi({ payment_log_id: id });
      message.success('Deleted');
      onReload();
    } catch {
      message.error('Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  const handleUpdateLogImage = async (id, file) => {
    setImgLoadingId(id);
    const fd = new FormData();
    fd.append('payment_log_id', id);
    fd.append('payment_image', file);
    try {
      await addKharchoPaymentLogImageApi(fd);
      message.success('Image updated');
      onReload();
    } catch {
      message.error('Failed to update image');
    } finally {
      setImgLoadingId(null);
    }
  };

  const handleAddPayment = async (form) => {
    setPayLoading(true);
    const fd = new FormData();
    fd.append('kharcho_id',   payKharchoId);
    fd.append('amount',       form.amount);
    fd.append('payment_date', form.date.format('YYYY-MM-DD'));
    if (form.note)  fd.append('note',          form.note);
    if (form.image) fd.append('payment_image', form.image);
    try {
      await addKharchoPaymentLogApi(fd);
      message.success('Payment added');
      setPayOpen(false);
      onReload();
    } catch {
      message.error('Failed to add payment');
    } finally {
      setPayLoading(false);
    }
  };

  const txnColumns = [
    {
      title: 'Type', dataIndex: 'type', width: 120,
      render: (v) => <Tag color={v === 'credit' ? 'green' : 'orange'}>{v === 'credit' ? 'Paid' : 'Outstanding'}</Tag>,
    },
    {
      title: 'Amount', dataIndex: 'amount',
      render: (v, r) => (
        <span style={{ fontWeight: 600, color: r.type === 'credit' ? '#52c41a' : '#ff4d4f' }}>
          {formatCurrency(v)}
        </span>
      ),
    },
    { title: 'Date', key: 'date', render: (_, r) => formatDate(r.credit_at || r.date) },
    { title: 'Remark', dataIndex: 'note', render: (v) => v || '—' },
    {
      title: 'Receipt', dataIndex: 'image', width: 70,
      render: (img, r) => {
        const src = r.image_url || (img ? PAYMENT_LOG_IMAGE(img) : null);
        return src ? (
          <div style={{ position: 'relative', width: 40, height: 40, flexShrink: 0 }}>
            <Image src={src} width={40} height={40} style={{ objectFit: 'cover', borderRadius: 4, display: 'block' }} />
            <label
              htmlFor={`sk-img-${r.id}`}
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute', bottom: 1, right: 1, width: 16, height: 16,
                background: 'rgba(0,0,0,0.7)', borderRadius: 2, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <CameraOutlined style={{ color: '#fff', fontSize: 9 }} />
            </label>
            <input
              id={`sk-img-${r.id}`}
              type="file" accept="image/*" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpdateLogImage(r.id, f); e.target.value = ''; }}
            />
          </div>
        ) : (
          <>
            <label
              htmlFor={`sk-img-${r.id}`}
              onClick={(e) => e.stopPropagation()}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid #d9d9d9', borderRadius: 4, padding: '2px 7px',
                cursor: 'pointer', background: '#fff', height: 24,
              }}
            >
              {imgLoadingId === r.id ? <Spin size="small" /> : <CameraOutlined style={{ fontSize: 12 }} />}
            </label>
            <input
              id={`sk-img-${r.id}`}
              type="file" accept="image/*" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpdateLogImage(r.id, f); e.target.value = ''; }}
            />
          </>
        );
      },
    },
    {
      title: '', key: 'actions', width: 50,
      render: (_, r) => r.type === 'credit' ? (
        <Popconfirm title="Delete this payment?" onConfirm={() => handleDeleteLog(r.id)} okButtonProps={{ danger: true }}>
          <Button size="small" danger icon={<DeleteOutlined />} loading={deletingId === r.id} />
        </Popconfirm>
      ) : null,
    },
  ];

  const expenseColumns = [
    {
      title: 'Crop', key: 'crop',
      render: (_, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {r.crop_image
            ? <img src={CROP_NAME_IMAGE(r.crop_image)} alt={r.crop} style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }} />
            : <span style={{ fontSize: 20 }}>🌾</span>
          }
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>
              {r.crop}{r.verity && <span style={{ fontWeight: 400, color: '#555', marginLeft: 4 }}>, {r.verity}</span>}
            </div>
            {r.plot_name && <div style={{ fontSize: 11, color: '#aaa' }}>📍 {r.plot_name}</div>}
          </div>
        </div>
      ),
    },
    { title: 'Date', dataIndex: 'expense_date', render: formatDate },
    { title: 'Amount', dataIndex: 'amount', render: (v) => <span style={{ fontWeight: 600 }}>{formatCurrency(v)}</span> },
    {
      title: 'Paid', key: 'paid',
      render: (_, r) => {
        const paid = (r.transactions || []).filter(t => t.type === 'credit').reduce((s, t) => s + Number(t.amount), 0);
        return <span style={{ color: '#52c41a' }}>{formatCurrency(paid || r.received_amount)}</span>;
      },
    },
    {
      title: 'Remaining', key: 'remaining',
      render: (_, r) => {
        const paid = (r.transactions || []).filter(t => t.type === 'credit').reduce((s, t) => s + Number(t.amount), 0);
        const rem = r.transactions ? Number(r.amount) - paid : Number(r.remaining);
        return <span style={{ color: rem > 0 ? '#fa8c16' : '#52c41a', fontWeight: 600 }}>{formatCurrency(rem)}</span>;
      },
    },
    {
      title: 'Status', dataIndex: 'payment_status',
      render: (v) => {
        const color = v === 'paid' ? 'green' : v === 'partial' ? 'blue' : 'orange';
        const label = v === 'paid' ? 'Paid' : v === 'partial' ? 'Partial' : 'Unpaid';
        return <Tag color={color}>{label}</Tag>;
      },
    },
  ];

  if (!expenses?.length) return <Empty description="No expenses" image={Empty.PRESENTED_IMAGE_SIMPLE} />;

  return (
    <>
      <Table
        dataSource={expenses}
        columns={expenseColumns}
        rowKey="kharcho_id"
        scroll={{ x: 650 }}
        expandable={{
          expandedRowKeys: expandedKeys,
          onExpand: (expanded, record) => {
            setExpandedKeys(expanded
              ? [...expandedKeys, record.kharcho_id]
              : expandedKeys.filter((k) => k !== record.kharcho_id)
            );
          },
          expandedRowRender: (record) => (
            <div style={{ padding: '8px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 12, color: '#888' }}>Transactions</span>
                {record.payment_status !== 'paid' && (
                  <Button
                    size="small" type="primary" icon={<PlusOutlined />}
                    style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}
                    onClick={() => {
                      const paid = (record.transactions || []).filter(t => t.type === 'credit').reduce((s, t) => s + Number(t.amount), 0);
                      setPayKharchoId(record.kharcho_id);
                      setPayRemaining(record.transactions ? Number(record.amount) - paid : Number(record.remaining));
                      setPayOpen(true);
                    }}
                  >
                    Add Payment
                  </Button>
                )}
              </div>
              <Table
                dataSource={record.transactions || []}
                columns={txnColumns}
                rowKey={(r, i) => `${r.kharch_id}_${r.type}_${i}`}
                pagination={false}
                size="small"
              />
            </div>
          ),
          rowExpandable: (record) => (record.transactions?.length > 0 || record.payment_status !== 'paid'),
        }}
      />
      <AddPaymentModal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        onSubmit={handleAddPayment}
        loading={payLoading}
        remaining={payRemaining}
      />
    </>
  );
}

// ── Main AccountDetail page ────────────────────────────────────────────────
export default function AccountDetail() {
  const { accountId } = useParams();
  const location      = useLocation();
  const navigate      = useNavigate();

  const accountType = location.state?.type;
  const accountName = location.state?.name;
  const backPath    = location.state?.from || '/accounts';

  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [saving, setSaving]       = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [detailTxn, setDetailTxn] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [showAccNum, setShowAccNum] = useState(false);

  const isBuyer      = accountType === 'buyer';
  const isGeneral    = accountType === 'general';
  const isShopKeeper = accountType === 'shop_keeper';
  const isBank       = accountType === 'bank';

  const fetchDetail = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      let res;
      if (isBuyer) {
        res = await getBuyerDetailApi(accountId);
        setData(res.data);
      } else if (isShopKeeper) {
        res = await getShopkeeperDetailApi({ account_id: accountId });
        setData(res.data);
      } else {
        res = await getAccountByIdApi(accountId, accountType ? { type: accountType } : undefined);
        setData(res.data?.data || res.data);
      }
    } catch {
      message.error('Failed to load account details');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [accountId, accountType, isBuyer, isShopKeeper]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const buyerAccount      = data?.account   || {};
  const buyerSales        = data?.sales     || [];
  const shopkeeperAccount = data?.account   || {};
  const shopkeeperExpenses = data?.expenses || [];
  const account      = (isBuyer || isShopKeeper) ? (data?.account || {}) : (data?.account || data || {});
  const transactions = (isBuyer || isShopKeeper) ? [] : (data?.transactions || []);

  const totalCredit = Number(account.total_credit) || 0;
  const totalDebit  = Math.abs(Number(account.total_debit) || 0);
  const outstanding = Number(buyerAccount.outstanding) || 0;
  const totalPaid   = transactions.reduce((s, t) => (t.payment_status || t.status) === 'paid'   ? s + Number(t.amount) : s, 0);
  const totalUnpaid = transactions.reduce((s, t) => (t.payment_status || t.status) === 'unpaid' ? s + Number(t.amount) : s, 0);
  const filtered    = statusFilter === 'all' ? transactions : transactions.filter((t) => (t.payment_status || t.status) === statusFilter);

  const bankInfo       = isBank && account.bank_details ? (decryptData(account.bank_details) || {}) : {};
  const bankTxnCredit  = isBank ? transactions.filter((t) => t.payment_status === 'credit').reduce((s, t) => s + Number(t.amount), 0) : 0;
  const bankTxnDebit   = isBank ? transactions.filter((t) => t.payment_status === 'debit').reduce((s, t) => s + Number(t.amount), 0) : 0;
  const bankBalance    = bankTxnCredit - bankTxnDebit;

  const printCount = isBuyer ? buyerSales.length
    : isShopKeeper ? shopkeeperExpenses.length
    : (isGeneral || isBank) ? transactions.length
    : filtered.length;

  const handlePrint = (orientation = 'portrait') => {
    let userName = '';
    try { userName = JSON.parse(localStorage.getItem('user'))?.name || ''; } catch { }
    const logoUrl = `${window.location.origin}/logo.png`;
    const titleName = accountName || account.name || 'Account';

    let columnsHtml = '';
    let rowsHtml = '';
    let summaryHtml = '';
    let bankInfoHtml = '';

    if (isBuyer) {
      columnsHtml = '<th>Crop</th><th>Date</th><th style="text-align:right">Amount</th><th style="text-align:right">Received</th><th style="text-align:right">Remaining</th><th>Status</th>';
      rowsHtml = buyerSales.map((r) => {
        const paid = (r.transactions || []).filter((t) => t.type === 'credit').reduce((s, t) => s + Number(t.amount), 0);
        const received = paid || Number(r.received_amount) || 0;
        const remaining = r.transactions ? Number(r.amount) - paid : Number(r.remaining) || 0;
        const cropLabel = `${r.crop || ''}${r.verity ? `, ${r.verity}` : ''}`;
        return `
          <tr>
            <td>${escapeHtml(cropLabel)}</td>
            <td>${formatDate(r.sell_date)}</td>
            <td style="text-align:right">${formatCurrency(r.amount)}</td>
            <td style="text-align:right;color:#16a34a">${formatCurrency(received)}</td>
            <td style="text-align:right;color:${remaining > 0 ? '#d97706' : '#16a34a'}">${formatCurrency(remaining)}</td>
            <td>${escapeHtml(SALE_STATUS_LABEL[r.payment_status] || r.payment_status || '—')}</td>
          </tr>`;
      }).join('');
      summaryHtml = `
        <div>Total Received: <strong style="color:#16a34a">${formatCurrency(totalCredit)}</strong></div>
        <div>Total Outstanding: <strong style="color:#dc2626">${formatCurrency(totalDebit)}</strong></div>
        <div>Remaining: <strong style="color:${outstanding > 0 ? '#d97706' : '#16a34a'}">${formatCurrency(outstanding)}</strong></div>`;
    } else if (isShopKeeper) {
      columnsHtml = '<th>Crop</th><th>Date</th><th style="text-align:right">Amount</th><th style="text-align:right">Paid</th><th style="text-align:right">Remaining</th><th>Status</th>';
      rowsHtml = shopkeeperExpenses.map((r) => {
        const paid = (r.transactions || []).filter((t) => t.type === 'credit').reduce((s, t) => s + Number(t.amount), 0);
        const paidAmt = paid || Number(r.received_amount) || 0;
        const remaining = r.transactions ? Number(r.amount) - paid : Number(r.remaining) || 0;
        const cropLabel = `${r.crop || ''}${r.verity ? `, ${r.verity}` : ''}`;
        const statusLabel = r.payment_status === 'paid' ? 'Paid' : r.payment_status === 'partial' ? 'Partial' : 'Unpaid';
        return `
          <tr>
            <td>${escapeHtml(cropLabel)}</td>
            <td>${formatDate(r.expense_date)}</td>
            <td style="text-align:right">${formatCurrency(r.amount)}</td>
            <td style="text-align:right;color:#16a34a">${formatCurrency(paidAmt)}</td>
            <td style="text-align:right;color:${remaining > 0 ? '#d97706' : '#16a34a'}">${formatCurrency(remaining)}</td>
            <td>${escapeHtml(statusLabel)}</td>
          </tr>`;
      }).join('');
      summaryHtml = `
        <div>Total Debit: <strong style="color:#dc2626">${formatCurrency(Number(shopkeeperAccount.total_debit) || 0)}</strong></div>
        <div>Total Paid: <strong style="color:#16a34a">${formatCurrency(Number(shopkeeperAccount.total_credit) || 0)}</strong></div>
        <div>Outstanding: <strong style="color:${Number(shopkeeperAccount.outstanding) > 0 ? '#d97706' : '#16a34a'}">${formatCurrency(Number(shopkeeperAccount.outstanding) || 0)}</strong></div>`;
    } else if (isBank) {
      columnsHtml = '<th>Type</th><th style="text-align:right">Amount</th><th>Note</th><th>Date</th>';
      rowsHtml = transactions.map((r) => `
        <tr>
          <td>${r.type === 'credit' ? 'Credit' : 'Debit'}</td>
          <td style="text-align:right;color:${r.type === 'credit' ? '#16a34a' : '#dc2626'};font-weight:600">${r.type === 'credit' ? '+' : '−'} ${formatCurrency(r.amount)}</td>
          <td>${escapeHtml(r.note || '—')}</td>
          <td>${formatDate(r.date || r.credit_at || r.created_at)}</td>
        </tr>`).join('');
      summaryHtml = `
        <div>Total Credits: <strong style="color:#16a34a">${formatCurrency(bankTxnCredit)}</strong></div>
        <div>Total Debits: <strong style="color:#dc2626">${formatCurrency(bankTxnDebit)}</strong></div>
        <div>Current Balance: <strong style="color:${bankBalance >= 0 ? '#0891b2' : '#dc2626'}">${bankBalance >= 0 ? '' : '−'}${formatCurrency(Math.abs(bankBalance))}</strong></div>`;
      const maskedAcc = bankInfo.account_number
        ? `•••• •••• ${String(bankInfo.account_number).slice(-4)}`
        : '—';
      bankInfoHtml = `
        <div class="bank-info">
          <div class="bank-info-row"><span>Bank Name</span><strong>${escapeHtml(bankInfo.bank_name || '—')}</strong></div>
          <div class="bank-info-row"><span>Account Holder</span><strong>${escapeHtml(bankInfo.account_holder_name || '—')}</strong></div>
          <div class="bank-info-row"><span>Account Number</span><strong>${escapeHtml(maskedAcc)}</strong></div>
          <div class="bank-info-row"><span>IFSC Code</span><strong>${escapeHtml(bankInfo.ifsc_code || '—')}</strong></div>
          <div class="bank-info-row"><span>Opening Balance</span><strong>${formatCurrency(Number(account.opening_balance) || 0)}</strong></div>
          ${account.note ? `<div class="bank-info-row"><span>Note</span><strong>${escapeHtml(account.note)}</strong></div>` : ''}
        </div>`;
    } else if (isGeneral) {
      columnsHtml = '<th>Type</th><th style="text-align:right">Amount</th><th>Note</th><th>Date</th>';
      rowsHtml = transactions.map((r) => `
        <tr>
          <td>${r.type === 'credit' ? 'Credit' : 'Debit'}</td>
          <td style="text-align:right;color:${r.type === 'credit' ? '#16a34a' : '#dc2626'};font-weight:600">${r.type === 'credit' ? '+' : '−'} ${formatCurrency(r.amount)}</td>
          <td>${escapeHtml(r.note || '—')}</td>
          <td>${formatDate(r.date || r.created_at)}</td>
        </tr>`).join('');
      summaryHtml = `
        <div>Total Credit: <strong style="color:#16a34a">${formatCurrency(totalCredit)}</strong></div>
        <div>Total Debit: <strong style="color:#dc2626">${formatCurrency(totalDebit)}</strong></div>
        <div>Balance: <strong style="color:${totalCredit - totalDebit >= 0 ? '#1d4ed8' : '#dc2626'}">${totalCredit - totalDebit >= 0 ? '+' : '−'}${formatCurrency(Math.abs(totalCredit - totalDebit))}</strong></div>`;
    } else {
      // labour (default) — respects the on-screen status filter
      columnsHtml = '<th style="text-align:right">Amount</th><th>Type / Note</th><th>Crop</th><th>Date</th><th>Status</th>';
      rowsHtml = filtered.map((r) => {
        const s = r.payment_status || r.status;
        return `
          <tr>
            <td style="text-align:right;color:#d97706;font-weight:600">${formatCurrency(r.amount)}</td>
            <td>${escapeHtml(r.note || r.expense_type || r.type || '—')}</td>
            <td>${escapeHtml(r.crop_name || '—')}</td>
            <td>${formatDate(r.date || r.created_at)}</td>
            <td>${escapeHtml(STATUS_LABEL[s] || s || '—')}</td>
          </tr>`;
      }).join('');
      summaryHtml = `
        <div>Total Paid: <strong style="color:#16a34a">${formatCurrency(totalPaid)}</strong></div>
        <div>Total Unpaid: <strong style="color:#d97706">${formatCurrency(totalUnpaid)}</strong></div>`;
    }

    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head>
      <title>${escapeHtml(titleName)} — Account Statement — Kheti Hisab</title>
      <style>
        @page { size: A4 ${orientation}; margin: 12mm; }
        * { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        body { font-family: Arial, sans-serif; font-size: 13px; padding: 28px 32px; color: #111; }
        .brand { display: flex; align-items: center; gap: 12px; margin-bottom: 4px; }
        .brand img { width: 40px; height: 40px; object-fit: contain; }
        .brand-name { font-size: 22px; font-weight: 700; color: #1a6b2a; letter-spacing: -0.3px; }
        .brand-tagline { font-size: 11px; color: #6b7280; margin-top: 1px; }
        .divider { border: none; border-top: 2px solid #1a6b2a; margin: 12px 0 16px; }
        .report-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 16px; }
        .report-title { font-size: 16px; font-weight: 700; color: #111; text-transform: capitalize; }
        .report-meta { font-size: 11px; color: #6b7280; text-align: right; line-height: 1.6; }
        .bank-info { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 16px; }
        .bank-info-row { background: #f0fdfa; border: 1px solid #99f6e4; border-radius: 6px; padding: 8px 12px; font-size: 12px; display: flex; flex-direction: column; gap: 2px; }
        .bank-info-row span { color: #6b7280; font-size: 11px; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #1a6b2a; color: #fff; padding: 8px 10px; text-align: left; font-size: 12px; }
        td { padding: 7px 10px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
        tbody tr:nth-child(odd) td { background: #ffffff; }
        tbody tr:nth-child(even) td { background: #eef5ec; }
        .totals { margin-top: 16px; display: flex; gap: 40px; font-size: 13px; padding: 12px 14px; background: #f9fafb; border-radius: 6px; border: 1px solid #e5e7eb; }
        .footer { margin-top: 28px; text-align: center; font-size: 11px; color: #9ca3af; border-top: 1px solid #e5e7eb; padding-top: 10px; }
        @media print { button { display: none; } }
      </style>
    </head><body>
      <div class="brand">
        <img src="${logoUrl}" alt="Kheti Hisab" onerror="this.style.display='none'" />
        <div>
          <div class="brand-name">Kheti Hisab</div>
          <div class="brand-tagline">Smart Farm Management</div>
        </div>
      </div>
      <hr class="divider" />
      <div class="report-header">
        <div class="report-title">${escapeHtml(titleName)} — Account Statement</div>
        <div class="report-meta">
          ${userName ? `<div>Account: <strong>${escapeHtml(userName)}</strong></div>` : ''}
          <div>Printed: ${dayjs().format('DD MMM YYYY, hh:mm A')}</div>
          <div>Entries: ${printCount}</div>
        </div>
      </div>
      ${bankInfoHtml}
      <table>
        <thead><tr>${columnsHtml}</tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <div class="totals">${summaryHtml}</div>
      <div class="footer">khethisab.shop &nbsp;·&nbsp; Kheti Hisab — Smart Farm Management</div>
      <script>window.onload = () => { window.print(); }<\/script>
    </body></html>`);
    win.document.close();
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      if (editing?.id) {
        await updateTransactionApi(editing.id, values);
        message.success('Transaction updated');
      } else {
        const payload = isBank ? { ...values, account_type: 'bank' } : values;
        await addTransactionApi(accountId, payload);
        message.success('Transaction added');

        if (isBank) {
          try {
            await createDailyExpenseApi({
              title:           values.note || accountName || account.name,
              amount:          String(values.amount),
              type:            values.type === 'debit' ? 'expense' : 'income',
              date:            values.date,
              bank_account_id: Number(accountId),
            });
          } catch {
            // daily expense mirror failure should not block the transaction
          }
        }
      }
      setModalOpen(false);
      setEditing(null);
      fetchDetail();
    } catch {
      message.error('Failed to save transaction');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteTransactionApi(id);
      message.success('Deleted');
      fetchDetail();
    } catch {
      message.error('Failed to delete');
    }
  };

  const handleStatusChange = async (txn, newStatus) => {
    setStatusLoading(true);
    try {
      if (accountType === 'labour') {
        await updateTransactionStatusApi({ transaction_id: txn.id, status: newStatus });
      } else {
        await changeExpensePaymentStatusApi({ expense_id: txn.id, status: newStatus });
      }
      message.success('Status updated');
      setDetailTxn(null);
      fetchDetail();
    } catch {
      message.error('Failed to update status');
    } finally {
      setStatusLoading(false);
    }
  };

  const generalColumns = [
    {
      title: 'Type', dataIndex: 'type', width: 100,
      render: (v) => <Tag color={v === 'credit' ? 'green' : 'red'}>{v === 'credit' ? 'Credit' : 'Debit'}</Tag>,
    },
    {
      title: 'Amount', dataIndex: 'amount',
      render: (v, r) => (
        <span style={{ fontWeight: 600, color: r.type === 'credit' ? '#52c41a' : '#ff4d4f' }}>
          {r.type === 'credit' ? '+' : '−'} {formatCurrency(v)}
        </span>
      ),
    },
    { title: 'Note', dataIndex: 'note', render: (v) => v || '—' },
    { title: 'Date', dataIndex: 'date', render: (v, r) => formatDate(v || r.created_at) },
    {
      title: 'Actions', key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />}
            onClick={() => { setEditing(record); setModalOpen(true); }} />
          <Popconfirm title="Delete this transaction?" onConfirm={() => handleDelete(record.id)} okButtonProps={{ danger: true }}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const labourColumns = [
    {
      title: 'Amount', dataIndex: 'amount',
      render: (v) => <span style={{ fontWeight: 600, color: '#fa8c16' }}>{formatCurrency(v)}</span>,
    },
    { title: 'Type / Note', key: 'note', render: (_, r) => r.note || r.expense_type || r.type || '—' },
    { title: 'Crop', dataIndex: 'crop_name', render: (v) => v || '—' },
    { title: 'Date', dataIndex: 'date', render: (v, r) => formatDate(v || r.created_at) },
    {
      title: 'Status', key: 'status',
      render: (_, r) => {
        const s = r.payment_status || r.status;
        return (
          <Tag color={STATUS_COLOR[s] || 'default'} style={{ cursor: 'pointer' }} onClick={() => setDetailTxn(r)}>
            {STATUS_LABEL[s] || s}
          </Tag>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(backPath)} />
        <Title level={4} style={{ margin: 0, color: '#1a6b2a', textTransform: 'capitalize', flex: 1 }}>
          {accountName || account.name || 'Account Detail'}
        </Title>
        <Dropdown
          disabled={printCount === 0}
          menu={{
            items: [
              { key: 'portrait', label: 'Print — Portrait' },
              { key: 'landscape', label: 'Print — Landscape' },
            ],
            onClick: ({ key }) => handlePrint(key),
          }}
        >
          <Tooltip title="Print account statement">
            <Button icon={<PrinterOutlined />} disabled={printCount === 0} />
          </Tooltip>
        </Dropdown>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 64 }}><Spin /></div>
      ) : (
        <>
          {isBank && (
            <Card style={{ borderRadius: 10, marginBottom: 16, background: 'linear-gradient(135deg, #e6fffb 0%, #f0f9ff 100%)', border: '1px solid #87e8de' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: '#13c2c2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <BankOutlined style={{ fontSize: 22, color: '#fff' }} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#006d75' }}>{bankInfo.bank_name || '—'}</div>
                  <div style={{ fontSize: 13, color: '#08979c' }}>{bankInfo.account_holder_name || '—'}</div>
                </div>
                <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                  <div style={{ fontSize: 12, color: '#888' }}>Current Balance</div>
                  <div style={{ fontWeight: 800, fontSize: 22, color: bankBalance >= 0 ? '#52c41a' : '#ff4d4f' }}>
                    {bankBalance >= 0 ? '' : '−'}{formatCurrency(Math.abs(bankBalance))}
                  </div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div style={{ background: 'rgba(255,255,255,0.7)', borderRadius: 8, padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Account Number</div>
                  <div style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 600, letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {showAccNum
                      ? bankInfo.account_number || '—'
                      : bankInfo.account_number ? '•••• •••• ' + String(bankInfo.account_number).slice(-4) : '—'
                    }
                    <Button
                      type="text" size="small"
                      icon={showAccNum ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                      onClick={() => setShowAccNum((v) => !v)}
                      style={{ padding: 0, height: 'auto', color: '#08979c' }}
                    />
                  </div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.7)', borderRadius: 8, padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>IFSC Code</div>
                  <div style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 600, letterSpacing: 1 }}>
                    {bankInfo.ifsc_code || '—'}
                  </div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.7)', borderRadius: 8, padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Opening Balance</div>
                  <div style={{ fontWeight: 600, color: '#595959' }}>{formatCurrency(Number(account.opening_balance) || 0)}</div>
                </div>
                {account.note && (
                  <div style={{ background: 'rgba(255,255,255,0.7)', borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Note</div>
                    <div style={{ fontSize: 13, color: '#595959' }}>{account.note}</div>
                  </div>
                )}
              </div>
            </Card>
          )}

          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            {isBuyer ? (
              <>
                <Card style={{ borderRadius: 8, background: '#f6ffed', border: 'none', flex: 1 }} bodyStyle={{ padding: '8px 16px' }}>
                  <Statistic title="Total Received" value={formatCurrency(totalCredit)} valueStyle={{ color: '#52c41a', fontSize: 20 }} />
                </Card>
                <Card style={{ borderRadius: 8, background: '#fff1f0', border: 'none', flex: 1 }} bodyStyle={{ padding: '8px 16px' }}>
                  <Statistic title="Total Outstanding" value={formatCurrency(totalDebit)} valueStyle={{ color: '#ff4d4f', fontSize: 20 }} />
                </Card>
                <Card style={{ borderRadius: 8, background: '#fff7e6', border: 'none', flex: 1 }} bodyStyle={{ padding: '8px 16px' }}>
                  <Statistic title="Remaining" value={formatCurrency(outstanding)} valueStyle={{ color: outstanding > 0 ? '#fa8c16' : '#52c41a', fontSize: 20 }} />
                </Card>
              </>
            ) : isShopKeeper ? (
              <>
                <Card style={{ borderRadius: 8, background: '#fff1f0', border: 'none', flex: 1 }} bodyStyle={{ padding: '8px 16px' }}>
                  <Statistic title="Total Debit" value={formatCurrency(Number(shopkeeperAccount.total_debit) || 0)} valueStyle={{ color: '#ff4d4f', fontSize: 20 }} />
                </Card>
                <Card style={{ borderRadius: 8, background: '#f6ffed', border: 'none', flex: 1 }} bodyStyle={{ padding: '8px 16px' }}>
                  <Statistic title="Total Paid" value={formatCurrency(Number(shopkeeperAccount.total_credit) || 0)} valueStyle={{ color: '#52c41a', fontSize: 20 }} />
                </Card>
                <Card style={{ borderRadius: 8, background: '#fff7e6', border: 'none', flex: 1 }} bodyStyle={{ padding: '8px 16px' }}>
                  <Statistic title="Outstanding" value={formatCurrency(Number(shopkeeperAccount.outstanding) || 0)} valueStyle={{ color: Number(shopkeeperAccount.outstanding) > 0 ? '#fa8c16' : '#52c41a', fontSize: 20 }} />
                </Card>
              </>
            ) : isGeneral ? (
              <>
                <Card style={{ borderRadius: 8, background: '#f6ffed', border: 'none', flex: 1 }} bodyStyle={{ padding: '8px 16px' }}>
                  <Statistic title="Total Credit" value={formatCurrency(totalCredit)} valueStyle={{ color: '#52c41a', fontSize: 20 }} />
                </Card>
                <Card style={{ borderRadius: 8, background: '#fff1f0', border: 'none', flex: 1 }} bodyStyle={{ padding: '8px 16px' }}>
                  <Statistic title="Total Debit" value={formatCurrency(totalDebit)} valueStyle={{ color: '#ff4d4f', fontSize: 20 }} />
                </Card>
                <Card style={{ borderRadius: 8, background: '#e6f7ff', border: 'none', flex: 1 }} bodyStyle={{ padding: '8px 16px' }}>
                  <Statistic title="Balance"
                    value={formatCurrency(Math.abs(totalCredit - totalDebit))}
                    prefix={totalCredit - totalDebit >= 0 ? '+' : '−'}
                    valueStyle={{ color: totalCredit - totalDebit >= 0 ? '#1890ff' : '#ff4d4f', fontSize: 20 }} />
                </Card>
              </>
            ) : isBank ? (
              <>
                <Card style={{ borderRadius: 8, background: '#f6ffed', border: 'none', flex: 1 }} bodyStyle={{ padding: '8px 16px' }}>
                  <Statistic title="Total Credits" value={formatCurrency(bankTxnCredit)} valueStyle={{ color: '#52c41a', fontSize: 20 }} />
                </Card>
                <Card style={{ borderRadius: 8, background: '#fff1f0', border: 'none', flex: 1 }} bodyStyle={{ padding: '8px 16px' }}>
                  <Statistic title="Total Debits" value={formatCurrency(bankTxnDebit)} valueStyle={{ color: '#ff4d4f', fontSize: 20 }} />
                </Card>
                <Card style={{ borderRadius: 8, background: '#e6fffb', border: 'none', flex: 1 }} bodyStyle={{ padding: '8px 16px' }}>
                  <Statistic title="Current Balance"
                    value={formatCurrency(Math.abs(bankBalance))}
                    prefix={bankBalance >= 0 ? '' : '−'}
                    valueStyle={{ color: bankBalance >= 0 ? '#13c2c2' : '#ff4d4f', fontSize: 20 }} />
                </Card>
              </>
            ) : (
              <>
                <Card style={{ borderRadius: 8, background: '#f6ffed', border: 'none', flex: 1 }} bodyStyle={{ padding: '8px 16px' }}>
                  <Statistic title="Total Paid" value={formatCurrency(totalPaid)} valueStyle={{ color: '#52c41a', fontSize: 20 }} />
                </Card>
                <Card style={{ borderRadius: 8, background: '#fff7e6', border: 'none', flex: 1 }} bodyStyle={{ padding: '8px 16px' }}>
                  <Statistic title="Total Unpaid" value={formatCurrency(totalUnpaid)} valueStyle={{ color: '#fa8c16', fontSize: 20 }} />
                </Card>
              </>
            )}
          </div>

          {isBuyer ? (
            <Card style={{ borderRadius: 10 }}>
              <BuyerSales sales={buyerSales} onReload={() => fetchDetail(true)} />
            </Card>
          ) : isShopKeeper ? (
            <Card style={{ borderRadius: 10 }}>
              <ShopkeeperExpenses expenses={shopkeeperExpenses} onReload={() => fetchDetail(true)} />
            </Card>
          ) : isBank ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontWeight: 600, color: '#595959' }}>Transactions</span>
                <Button type="primary" icon={<PlusOutlined />}
                  style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}
                  onClick={() => { setEditing(null); setModalOpen(true); }}>
                  Add Transaction
                </Button>
              </div>
              <Card style={{ borderRadius: 10 }}>
                {transactions.length === 0 ? (
                  <Empty description="No transactions yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  <Table
                    dataSource={transactions}
                    columns={[
                      {
                        title: 'Type', dataIndex: 'type', width: 110,
                        render: (v) => <Tag color={v === 'credit' ? 'green' : 'red'}>{v === 'credit' ? 'Credit' : 'Debit'}</Tag>,
                      },
                      {
                        title: 'Amount', dataIndex: 'amount',
                        render: (v, r) => (
                          <span style={{ fontWeight: 700, color: r.type === 'credit' ? '#52c41a' : '#ff4d4f' }}>
                            {r.type === 'credit' ? '+' : '−'} {formatCurrency(v)}
                          </span>
                        ),
                      },
                      { title: 'Note', dataIndex: 'note', render: (v) => v || '—' },
                      { title: 'Date', dataIndex: 'date', render: (v, r) => formatDate(v || r.credit_at || r.created_at) },
                      {
                        title: 'Actions', key: 'actions',
                        render: (_, record) => (
                          <Space>
                            <Button size="small" icon={<EditOutlined />}
                              onClick={() => { setEditing(record); setModalOpen(true); }} />
                            <Popconfirm title="Delete this transaction?" onConfirm={() => handleDelete(record.id)} okButtonProps={{ danger: true }}>
                              <Button size="small" danger icon={<DeleteOutlined />} />
                            </Popconfirm>
                          </Space>
                        ),
                      },
                    ]}
                    rowKey="id"
                    scroll={{ x: 500 }}
                  />
                )}
              </Card>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12 }}>
                {!isGeneral && (
                  <Select value={statusFilter} onChange={setStatusFilter} options={STATUS_OPTIONS} style={{ width: 130 }} />
                )}
                <div style={{ flex: 1 }} />
                {isGeneral && (
                  <Button type="primary" icon={<PlusOutlined />}
                    style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}
                    onClick={() => { setEditing(null); setModalOpen(true); }}>
                    Add Transaction
                  </Button>
                )}
              </div>
              <Card style={{ borderRadius: 10 }}>
                {(isGeneral ? transactions : filtered).length === 0 ? (
                  <Empty description="No transactions" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  <Table
                    dataSource={isGeneral ? transactions : filtered}
                    columns={isGeneral ? generalColumns : labourColumns}
                    rowKey="id"
                    scroll={{ x: 500 }}
                    onRow={!isGeneral ? (record) => ({ onClick: () => setDetailTxn(record), style: { cursor: 'pointer' } }) : undefined}
                  />
                )}
              </Card>
            </>
          )}
        </>
      )}

      <TransactionModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSubmit={handleSave}
        initialValues={editing}
        loading={saving}
      />
      <PaymentDetailModal
        open={!!detailTxn}
        onClose={() => setDetailTxn(null)}
        txn={detailTxn}
        accountType={accountType}
        onStatusChange={handleStatusChange}
        statusLoading={statusLoading}
      />
    </div>
  );
}
